import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { SectionHeading } from "@/components/section-heading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  getOpsAlertSettings,
  updateOpsAlertSettings,
} from "@/lib/payments/admin-tips.functions";
import { sendOpsAlertTestEmail } from "@/lib/payments/webhook-ops.functions";

import {
  DEFAULT_OPS_SETTINGS,
  isQuietHour,
  normaliseRecipients,
  type OpsAlertSettings,
} from "@/lib/payments/ops-settings";

export const Route = createFileRoute("/_authenticated/admin/ops-settings")({
  head: () => ({
    meta: [
      { title: "Alert thresholds — Quibblies admin" },
      {
        name: "description",
        content:
          "Configure when Quibblies raises payment reconciliation and webhook lag alerts, and set quiet hours for overnight notifications.",
      },
      { property: "og:title", content: "Alert thresholds — Quibblies admin" },
      {
        property: "og:description",
        content: "Tune payment alerting thresholds and quiet hours.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: OpsSettingsPage,
});

const NUMBERS: Array<{
  key: keyof OpsAlertSettings;
  label: string;
  hint: string;
  min: number;
  max: number;
}> = [
  {
    key: "mismatchesPerSweep",
    label: "Mismatches per sweep",
    hint: "How many tips the hourly reconciliation may repair before it counts as repeated drift.",
    min: 1,
    max: 500,
  },
  {
    key: "pendingLagMinutes",
    label: "Pending lag (minutes)",
    hint: "How long a tip may sit pending after checkout before webhooks are considered late.",
    min: 5,
    max: 1440,
  },
  {
    key: "laggingTips",
    label: "Lagging tips before alert",
    hint: "How many late-pending tips must pile up before the lag alert fires.",
    min: 1,
    max: 500,
  },
  {
    key: "silenceHours",
    label: "Webhook silence (hours)",
    hint: "Hours with no provider event at all, while tips were still being started.",
    min: 1,
    max: 168,
  },
  {
    key: "dedupeHours",
    label: "Repeat window (hours)",
    hint: "The same alert is not raised again inside this window.",
    min: 1,
    max: 168,
  },
];

function OpsSettingsPage() {
  const queryClient = useQueryClient();
  const load = useServerFn(getOpsAlertSettings);
  const save = useServerFn(updateOpsAlertSettings);
  const [draft, setDraft] = React.useState<OpsAlertSettings | null>(null);
  const [recipientsText, setRecipientsText] = React.useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["ops-alert-settings"],
    queryFn: () => load(),
    retry: false,
  });

  React.useEffect(() => {
    if (data?.settings) {
      setDraft(data.settings);
      setRecipientsText(data.settings.incidentEmailRecipients.join(", "));
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (next: OpsAlertSettings) => save({ data: next as unknown as Record<string, unknown> }),
    onSuccess: (result) => {
      toast.success("Alert settings saved");
      setDraft(result.settings);
      setRecipientsText(result.settings.incidentEmailRecipients.join(", "));
      void queryClient.invalidateQueries({ queryKey: ["ops-alert-settings"] });
    },
    onError: (err: Error) => toast.error("Could not save", { description: err.message }),
  });

  const sendTest = useServerFn(sendOpsAlertTestEmail);
  const testEmail = useMutation({
    mutationFn: () => sendTest(),
    onSuccess: (result) =>
      result.sent > 0
        ? toast.success("Test alert sent", { description: result.message })
        : toast.warning("Nothing was delivered", { description: result.message }),
    onError: (err: Error) => toast.error("Could not send the test", { description: err.message }),
  });

  const values = draft ?? DEFAULT_OPS_SETTINGS;

  const quietNow = isQuietHour(values);

  function set<K extends keyof OpsAlertSettings>(key: K, value: OpsAlertSettings[K]) {
    setDraft({ ...values, [key]: value });
  }

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <SectionHeading
          title="Alert thresholds"
          description="This area is restricted to administrators."
        />
        <p className="mt-4 text-sm text-destructive">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Alert thresholds"
        description="Decide how much payment drift or webhook delay is tolerated before staff are alerted, and when those alerts should stay quiet."
      />

      {isLoading ? (
        <Skeleton className="mt-8 h-80 w-full rounded-2xl" />
      ) : (
        <>
          <div className="mt-8 space-y-3">
            {NUMBERS.map((field) => (
              <label
                key={String(field.key)}
                className="flex items-start justify-between gap-4 rounded-2xl border border-border/60 bg-surface/50 p-5"
              >
                <span>
                  <span className="block text-sm font-medium text-foreground">{field.label}</span>
                  <span className="mt-1 block text-sm text-muted-foreground">{field.hint}</span>
                </span>
                <Input
                  type="number"
                  className="w-24"
                  min={field.min}
                  max={field.max}
                  value={String(values[field.key])}
                  onChange={(event) =>
                    set(field.key, Number(event.target.value) as never)
                  }
                />
              </label>
            ))}
          </div>

          <div className="mt-8 space-y-3">
            <label className="flex items-start justify-between gap-4 rounded-2xl border border-border/60 bg-surface/50 p-5">
              <span>
                <span className="block text-sm font-medium text-foreground">Quiet hours</span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  Alerts are still recorded and shown on the finance dashboard — only the
                  notification is held back. Times are UTC.
                  {quietNow ? " Quiet hours are active right now." : ""}
                </span>
              </span>
              <Switch
                checked={values.quietHoursEnabled}
                onCheckedChange={(checked) => set("quietHoursEnabled", checked)}
              />
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex items-center justify-between gap-4 rounded-2xl border border-border/60 bg-surface/50 p-5">
                <span className="text-sm text-foreground">Quiet from (hour, UTC)</span>
                <Input
                  type="number"
                  className="w-20"
                  min={0}
                  max={23}
                  value={String(values.quietStartHour)}
                  onChange={(event) => set("quietStartHour", Number(event.target.value))}
                />
              </label>
              <label className="flex items-center justify-between gap-4 rounded-2xl border border-border/60 bg-surface/50 p-5">
                <span className="text-sm text-foreground">Quiet until (hour, UTC)</span>
                <Input
                  type="number"
                  className="w-20"
                  min={0}
                  max={23}
                  value={String(values.quietEndHour)}
                  onChange={(event) => set("quietEndHour", Number(event.target.value))}
                />
              </label>
            </div>

            <label className="flex items-start justify-between gap-4 rounded-2xl border border-border/60 bg-surface/50 p-5">
              <span>
                <span className="block text-sm font-medium text-foreground">
                  Silence critical alerts too
                </span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  Off by default: a real outage should still reach somebody overnight.
                </span>
              </span>
              <Switch
                checked={values.quietMutesCritical}
                onCheckedChange={(checked) => set("quietMutesCritical", checked)}
              />
            </label>
          </div>

          <div className="mt-8 space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Webhook failure &amp; tip-retry alerts
            </h2>

            <label className="flex items-start justify-between gap-4 rounded-2xl border border-border/60 bg-surface/50 p-5">
              <span>
                <span className="block text-sm font-medium text-foreground">
                  Repeat window for incidents (hours)
                </span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  A burst of bad callbacks raises one alert. Kept shorter than the sweep
                  window so a genuine outage is reported again quickly.
                </span>
              </span>
              <Input
                type="number"
                className="w-24"
                min={1}
                max={168}
                value={String(values.incidentDedupeHours)}
                onChange={(event) => set("incidentDedupeHours", Number(event.target.value))}
              />
            </label>

            <label className="flex items-start justify-between gap-4 rounded-2xl border border-border/60 bg-surface/50 p-5">
              <span>
                <span className="block text-sm font-medium text-foreground">
                  Ignore quiet hours for incidents
                </span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  A rejected callback or a tip that only succeeded after a failure is money
                  at risk, so it reaches staff even overnight.
                </span>
              </span>
              <Switch
                checked={values.incidentIgnoresQuietHours}
                onCheckedChange={(checked) => set("incidentIgnoresQuietHours", checked)}
              />
            </label>

            <label className="flex items-start justify-between gap-4 rounded-2xl border border-border/60 bg-surface/50 p-5">
              <span>
                <span className="block text-sm font-medium text-foreground">
                  Email incident alerts
                </span>
                <span className="mt-1 block text-sm text-muted-foreground">
                  In-app alerts and the record are kept either way; this only controls email.
                </span>
              </span>
              <Switch
                checked={values.incidentEmailEnabled}
                onCheckedChange={(checked) => set("incidentEmailEnabled", checked)}
              />
            </label>

            <div className="rounded-2xl border border-border/60 bg-surface/50 p-5">
              <label className="block text-sm font-medium text-foreground" htmlFor="ops-recipients">
                Extra email recipients
              </label>
              <p className="mt-1 text-sm text-muted-foreground">
                Administrators and the Owner are always emailed. Add a finance or on-call
                inbox here, separated by commas. Invalid addresses are dropped on save.
              </p>
              <Input
                id="ops-recipients"
                className="mt-3"
                placeholder="finance@quibblies.com, oncall@quibblies.com"
                value={recipientsText}
                onChange={(event) => setRecipientsText(event.target.value)}
              />
            </div>

            <div className="rounded-2xl border border-border/60 bg-surface/50 p-5">
              <p className="text-sm font-medium text-foreground">Send a test alert</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Sends a clearly-labelled test to exactly the people a webhook-failure or
                tip-retry alert would reach, using the saved settings, and reports what quiet
                hours and the repeat window would do to a real alert. Save first if you have
                just changed the recipients.
              </p>
              <Button
                className="mt-3"
                variant="outline"
                disabled={testEmail.isPending}
                onClick={() => testEmail.mutate()}
              >
                {testEmail.isPending ? "Sending…" : "Send test email"}
              </Button>
              {testEmail.data ? (
                <div className="mt-3 rounded-xl border border-border/60 bg-background/50 p-3 text-sm">
                  <p className="text-foreground">{testEmail.data.message}</p>
                  {testEmail.data.recipients.length > 0 ? (
                    <p className="mt-1 text-xs text-muted-foreground">
                      Recipients: {testEmail.data.recipients.join(", ")}
                    </p>
                  ) : null}
                  {testEmail.data.skipped.length > 0 ? (
                    <p className="mt-1 text-xs text-destructive">
                      Not delivered: {testEmail.data.skipped.join(", ")}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>



          <div className="mt-8 flex items-center gap-3">
            <Button
              onClick={() =>
                mutation.mutate({
                  ...values,
                  incidentEmailRecipients: normaliseRecipients(recipientsText),
                })
              }
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Saving…" : "Save thresholds"}
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setDraft(DEFAULT_OPS_SETTINGS);
                setRecipientsText("");
              }}
            >
              Reset to defaults
            </Button>
            {data?.updatedAt ? (
              <span className="text-xs text-muted-foreground">
                Last changed {new Date(data.updatedAt).toLocaleString()}
              </span>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}
